import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type RatioVerificationFields, type RatioVerificationRequest } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function RatioVerification({
  values, ct, vt, onConfirm, onVerified, disabled = false,
}: {
  values: RatioVerificationFields;
  ct: [number, number];
  vt: [number, number];
  onConfirm: (body: RatioVerificationRequest) => Promise<void>;
  onVerified?: () => Promise<void>;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("");
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: RatioVerificationRequest) => onConfirm(body),
    onSuccess: async () => {
      if (onVerified) {
        await onVerified();
      } else {
        // Feeder confirmation may affect cached previews of multiple controllers.
        await queryClient.cancelQueries({ queryKey: ["controller-config-preview"] });
        queryClient.removeQueries({ queryKey: ["controller-config-preview"] });
      }
      void queryClient.invalidateQueries({ queryKey: ["feeders"] });
      void queryClient.invalidateQueries({ queryKey: ["controller"] });
      setOpen(false);
      setSource("");
    },
  });
  return (
    <>
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setOpen(true)}>
        Verify CT/VT
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Engineering ratio confirmation</DialogTitle>
            <DialogDescription>
              Confirm only against engineering evidence. Default 1:1 and imported ratios are
              unverified until explicitly confirmed. Changing a ratio clears its confirmation.
              Confirmation is optional and informational; unverified ratios can be compiled and queued.
              This action does not queue or publish device configuration.
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="ratio-confirmation-source">Engineering evidence / confirmation source</Label>
          <Input id="ratio-confirmation-source" value={source} maxLength={512}
            onChange={(event) => setSource(event.target.value)} placeholder="Drawing, survey or engineer reference" />
          {(["ct", "vt"] as const).map((ratio) => {
            const [primary, secondary] = ratio === "ct" ? ct : vt;
            const state = values[`${ratio}_verification_state`] ?? "unverified";
            return (
              <div key={ratio} className="space-y-2">
                <p>{ratio.toUpperCase()} {primary}:{secondary} — {state}</p>
                {values[`${ratio}_verification_source`] ? <p className="text-sm text-muted-foreground">
                  {values[`${ratio}_verification_source`]}
                </p> : null}
                <Button type="button" disabled={!source.trim() || mutation.isPending}
                  onClick={() => mutation.mutate({ ratio, primary, secondary, state: "verified", source })}>
                  Explicitly confirm {ratio.toUpperCase()} {primary}:{secondary}
                </Button>
                {state === "verified" ? <Button type="button" variant="ghost" disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ ratio, primary, secondary, state: "unverified", source: "" })}>
                  Clear {ratio.toUpperCase()} confirmation
                </Button> : null}
              </div>
            );
          })}
          {mutation.error ? <p role="alert" className="text-sm text-destructive">{mutation.error.message}</p> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
